import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import React, { useEffect, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { ImUpload } from "react-icons/im";
import { AiOutlineClose, AiOutlineCloudUpload } from "react-icons/ai";
import { useFormContext } from "react-hook-form";
import toast from "react-hot-toast";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import ReactPlayer from "react-player";
import ReorderImageModal from "./ReorderImageModal";
import ReorderVideoModal from "./ReorderVideoModal";
import LexicalEditor from "./LexicalTextEditor/MyEditor";

export default function Memories({ data, link, onSubmit, handleSubmit }) {
  const {
    register,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext();

  const [acceptedVideoFiles, setAcceptedVideoFiles] = useState([]);
  const [acceptedImageFiles, setAcceptedImageFiles] = useState([]);
  const [acceptedPdfFiles, setAcceptedPdfFiles] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [reorderImageModal, setReorderImageModal] = useState(false);
  const [reorderVideoModal, setReorderVideoModal] = useState(false);
  const [imageText, setImageText] = useState("");
  const [freeText, setFreeText] = useState("");
  const [topImage, setTopImage] = useState(null);
  const [videoDescription, setVideoDescription] = useState("");
  
  useEffect(() => {
    let keys = [
      "title",
      "subTitle",
      "freeText",
      "videoUrl",
      "videoDescription",
      "imageText",
      "pdfTitle",
      "videos",
      "images",
      "topImage",
      "pdfs",
      "isProtected",
      "password",
    ];

    keys.forEach((key) => {
      if (key === "topImage") {
        // Handle topImage specially - existing data is a string URL
        const existingTopImage = data?.storedInfo?.memories?.[key];
        if (existingTopImage && typeof existingTopImage === 'string') {
          // Keep existing string format for backward compatibility
          setValue(key, existingTopImage);
          setTopImage(existingTopImage);
        } else {
          setValue(key, existingTopImage);
          setTopImage(existingTopImage?.url || existingTopImage || null);
        }
      } else {
        setValue(key, data?.storedInfo?.memories?.[key]);
      }
    });
    setImageText(data?.storedInfo?.memories?.imageText || "");
    setFreeText(data?.storedInfo?.memories?.freeText || "");
    setVideoDescription(data?.storedInfo?.memories?.videoDescription || "");
    setAcceptedVideoFiles(data?.storedInfo?.memories?.videos || []);
    setAcceptedImageFiles(data?.storedInfo?.memories?.images || []);
    setAcceptedPdfFiles(data?.storedInfo?.memories?.pdfs || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRemoveFile = (file, fileType) => {
    const updateFiles = (prevFiles) => {
      if (!prevFiles) return [];

      return prevFiles.filter((f) => {
        if (!f) return false;

        if (typeof f === "string") {
          return f !== file;
        }
        if (f instanceof File) {
          return file instanceof File ? f.name !== file.name : true;
        }

        if (f.url) {
          if (typeof f.url === "string") {
            return file?.url ? f.url !== file.url : f.url !== file;
          }
          if (typeof f.url.url === "string") {
            return file?.url?.url
              ? f.url.url !== file.url.url
              : f.url.url !== file.url;
          }
          if (f.url instanceof File) {
            return file instanceof File ? f.url.name !== file.name : true;
          }
        }

        return true;
      });
    };

    switch (fileType) {
      case "video":
        setAcceptedVideoFiles(updateFiles);
        setValue("videos", updateFiles(watch("videos")));
        break;
      case "image":
        setAcceptedImageFiles(updateFiles);
        setValue("images", updateFiles(watch("images")));
        break;
      case "topImage":
        setAcceptedImageFiles(updateFiles);
        setValue("topImage", updateFiles(watch("topImage")));
        break;
      case "pdf":
        setAcceptedPdfFiles(updateFiles);
        setValue("pdfs", updateFiles(watch("pdfs")));
        break;
      default:
        break;
    }
  };

  const { getRootProps: getVideoRootProps, getInputProps: getVideoInputProps } =
    useDropzone({
      onDrop: async (acceptedFiles, notAcceptedFile) => {
        if (notAcceptedFile.length > 0) {
          toast.error(notAcceptedFile[0].errors[0].message);
          return;
        }
        let files = watch("videos") || [];
        setValue("videos", [...files, ...acceptedFiles]);
        setAcceptedVideoFiles((prevFiles) => [...prevFiles, ...acceptedFiles]);
      },
      accept: { "video/*": [".mp4", ".mov", ".avi", ".wmv", ".flv", ".mkv"] },
      maxSize: 250 * 1024 * 1024, // 250mb
    });

  const { getRootProps: getImageRootProps, getInputProps: getImageInputProps } =
    useDropzone({
      onDrop: async (acceptedFiles, notAcceptedFile) => {
        if (notAcceptedFile.length > 0) {
          toast.error(notAcceptedFile[0].errors[0].message);
          return;
        }
        let imgs = watch("images") || [];
        setValue("images", [...imgs, ...acceptedFiles]);
        setAcceptedImageFiles((prevFiles) => [...prevFiles, ...acceptedFiles]);
      },
      accept: {
        "image/*": [".jpeg", ".png", ".jpg", ".webp", ".svg"],
      },
      maxSize: 100 * 1024 * 1024, // 100mb
    });

  const {
    getRootProps: getTopImageRootProps,
    getInputProps: getTopImageInputProps,
  } = useDropzone({
    onDrop: async (acceptedFiles, notAcceptedFiles) => {
      if (notAcceptedFiles.length > 0) {
        toast.error("Only one file item is allowed");
        return;
      }

      const file = acceptedFiles[0];
      if (!file) return;

      // If you just want the file name or URL string
      const fileUrl = URL.createObjectURL(file);

      // Store both the blob URL and original filename
      const topImageData = {
        url: fileUrl,
        originalName: file.name
      };

      // For react-hook-form single value
      setValue("topImage", topImageData, { shouldValidate: true });

      // Optional: update local state for preview
      setTopImage(fileUrl);
    },
    accept: {
      "image/*": [".jpeg", ".png", ".jpg", ".webp", ".svg"],
    },
    maxSize: 100 * 1024 * 1024,
    multiple: false,
    maxFiles: 1,
  });

  const { getRootProps: getPdfRootProps, getInputProps: getPdfInputProps } =
    useDropzone({
      onDrop: async (acceptedFiles, notAcceptedFile) => {
        if (notAcceptedFile.length > 0) {
          toast.error(notAcceptedFile[0].errors[0].message);
          return;
        }
        let pdfs = watch("pdfs") || [];
        setValue("pdfs", [...pdfs, ...acceptedFiles]);
        setAcceptedPdfFiles((prevFiles) => [...prevFiles, ...acceptedFiles]);
      },
      accept: {
        "application/pdf": [".pdf"],
      },
      maxSize: 100 * 1024 * 1024, // 100mb
    });
    
  const imageurl = watch("images") || [];
  const videourl = watch("videos") || [];

  const videoSources = useMemo(() => {
    return videourl?.map((file) => {
      if (typeof file.url === "string") return file.url;
      if (file instanceof File) return URL.createObjectURL(file);
      if (typeof file.url.url === "string") return file.url.url;
      if (file.url instanceof File) return URL.createObjectURL(file.url);
      return "";
    });
  }, [videourl]);

  // Revoke blob URLs on cleanup
  useEffect(() => {
    return () => {
      videoSources.forEach((src) => {
        if (src.startsWith("blob:")) {
          URL.revokeObjectURL(src);
        }
      });
    };
  }, [videoSources]);

  return (
    <>
      <section>
        <div>
          <h4>Title</h4>
          <input
            {...register("title")}
            className={`w-full text-sm mt-2 sm:text-base placeholder-gray-500 px-4 border-gray-400 rounded-lg border py-3 focus:outline-none focus:border-blue-400`}
            placeholder="E.g. In Loving Memory of John Doe"
          />
          {errors?.title && (
            <p className="text-red-500 text-xs mt-1">{errors.title.message}</p>
          )}
        </div>

        <div className="mt-4">
          <h4>Sub Title</h4>
          <input
            {...register("subTitle")}
            className={`w-full text-sm mt-2 sm:text-base placeholder-gray-500 px-4 border-gray-400 rounded-lg border py-3 focus:outline-none focus:border-blue-400`}
            placeholder="E.g. In Loving Memory of John Doe"
          />
          {errors?.subTitle && (
            <p className="text-red-500 text-xs mt-1">
              {errors.subTitle.message}
            </p>
          )}
        </div>
        <div className="mt-4">
          <h4>Top Image (Ideally upload image of 3:4 size) </h4>
          <div className="flex items-center space-x-2 pt-2">
            <div
              className={`flex w-full justify-center items-center h-44 border-2 bg-blue-50 border-tealBlue border-dashed rounded-lg p-4`}
              {...getTopImageRootProps()}
            >
              <input {...getTopImageInputProps()} />
              <div className="text-center">
                <ImUpload className="mx-auto text-tealBlue text-6xl" />
                <button
                  type="button"
                  className="mt-4 bg-tealBlue text-white px-6 py-2 rounded-lg hover:bg-tealBlue"
                >
                  Upload image
                </button>
                <p className="text-gray-600 text-sm font-bold mt-4">
                  Maximum size: 100MB
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center flex-wrap gap-3 mt-4">
            {topImage && (
              <div className="relative">
                <img
                  src={topImage} // works for both blob: and https:// URLs
                  alt="Top preview"
                  className="object-cover h-[80px] w-[80px]"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 p-1">
                  <div className="flex justify-between items-center">
                    <p className="text-white text-xs w-[80px] truncate">
                      {(() => {
                        const currentTopImage = watch("topImage");
                        if (typeof currentTopImage === 'object' && currentTopImage?.originalName) {
                          return currentTopImage.originalName;
                        }
                        return topImage.split("/").pop() || "Top Image";
                      })()}
                    </p>
                    <AiOutlineClose
                      className="text-red-500 cursor-pointer"
                      onClick={() => {
                        setTopImage(null);
                        setValue("topImage", null);
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="mt-4 mb-4">
          <h4>About</h4>
          <LexicalEditor
            setComment={(html) => {
              setValue("freeText", html);
            }}
            initialValue={freeText || ""}
          />
          {errors?.freeText && (
            <p className="text-red-500 text-xs mt-1">
              {errors.freeText.message}
            </p>
          )}
        </div>

        <div className="mt-4">
          <h4>Photos</h4>
          <div className="flex items-center space-x-2 pt-2">
            <div
              className={`flex w-full justify-center items-center h-44 border-2 bg-blue-50 border-tealBlue border-dashed rounded-lg p-4`}
              {...getImageRootProps()}
            >
              <input {...getImageInputProps()} />
              <div className="text-center">
                <ImUpload className="mx-auto text-tealBlue text-6xl" />
                <button
                  type="button"
                  className="mt-4 bg-tealBlue text-white px-6 py-2 rounded-lg hover:bg-tealBlue"
                >
                  Upload images
                </button>
                <p className="text-gray-600 text-sm font-bold mt-4">
                  Maximum size: 100MB
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center flex-wrap gap-3 mt-4">
            {imageurl &&
              imageurl?.map((file, index) => {
                const imageSrc =
                  typeof file.url === "string"
                    ? file.url
                    : file instanceof File
                    ? URL.createObjectURL(file)
                    : typeof file.url.url === "string"
                    ? file.url.url
                    : file.url instanceof File
                    ? URL.createObjectURL(file.url)
                    : "";

                const imageName =
                  typeof file.url === "string"
                    ? file.url.split("/").pop()
                    : file instanceof File
                    ? file.name
                    : typeof file.url.url === "string"
                    ? file.url.url.split("/").pop()
                    : file.url instanceof File
                    ? file.url.name
                    : "";

                return (
                  <div key={index} className="relative">
                    <img
                      src={imageSrc}
                      alt={typeof file === "string" ? "Image" : file.name}
                      height={100}
                      className="object-cover h-[80px] w-[80px]"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 p-1">
                      <div className="flex justify-between items-center">
                        <p className="text-white text-xs w-[80px] truncate">
                          {imageName}
                        </p>
                        <AiOutlineClose
                          className="text-red-500 cursor-pointer"
                          onClick={() =>
                            handleRemoveFile(
                              file instanceof File ? file : file.url,
                              "image"
                            )
                          }
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
          <div>
            <div className="flex items-center justify-between space-x-2">
              <button
                type="button"
                onClick={() => setReorderImageModal(true)}
                className="mt-4 bg-tealBlue text-white px-6 py-2 rounded-lg hover:bg-tealBlue text-[13px] sm:text-base whitespace-nowrap font-bold sm:font-normal"
              >
                Reorder Images
              </button>

              <button
                onClick={handleSubmit(async (data) => {
                  const previewWindow = window.open("", "_blank");
                  if (previewWindow) {
                    previewWindow.document.write(
                      "Saving and preparing page preview..."
                    );
                  }

                  await onSubmit(data);
                  if (link && previewWindow) {
                    previewWindow.location.href = `https://${link}`;
                  } else if (previewWindow) {
                    previewWindow.close();
                  }
                })}
                type="button"
                className="mt-4 inline-block w-[160px] text-center bg-tealBlue text-white px-6 py-2 rounded-lg hover:bg-tealBlue text-[13px] sm:text-base whitespace-nowrap font-bold sm:font-normal"
              >
                Preview
              </button>
            </div>
          </div>
        </div>

        <div className="mt-4">
          <h4>This text will appear above the images in the PHOTOS tab</h4>
          <div className="flex items-center space-x-2 pt-2">
            <div className="relative w-full">
              <LexicalEditor
                setComment={(html) => {
                  setValue("imageText", html);
                }}
                initialValue={imageText || ""}
              />
              {errors?.imageText && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.imageText.message}
                </p>
              )}
            </div>
          </div>
          {errors?.imageText && (
            <p className="text-red-500 text-xs mt-1">
              {errors.imageText.message}
            </p>
          )}
        </div>

        <div className="mt-4">
          <h4>
            Video URL{" "}
            <span className="text-xs text-gray-500">
              (only to fill if you want to share a Youtube video)
            </span>
          </h4>
          <div className="flex items-center space-x-2 pt-2">
            <input
              {...register("videoUrl")}
              className={`w-full text-sm sm:text-base placeholder-gray-500 px-4 border-gray-400 rounded-lg border py-3 focus:outline-none focus:border-blue-400`}
              placeholder="https://www.youtube.com/watch..."
            />
          </div>
          {errors?.videoUrl && (
            <p className="text-red-500 text-xs mt-1">
              {errors.videoUrl.message}
            </p>
          )}
        </div>

        <div className="mt-4">
          <h4>You can also upload video(s) from your device</h4>
          <div className="flex items-center space-x-2 pt-2">
            <div
              className={`flex w-full justify-center items-center h-44 border-2 bg-blue-50 border-tealBlue border-dashed rounded-lg p-4`}
              {...getVideoRootProps()}
            >
              <input {...getVideoInputProps()} />
              <div className="text-center">
                <AiOutlineCloudUpload className="mx-auto text-tealBlue text-6xl" />
                <button
                  type="button"
                  className="mt-4 bg-tealBlue text-white px-6 py-2 rounded-lg hover:bg-tealBlue"
                >
                  Upload video(s)
                </button>
                <p className="text-gray-600 text-sm font-bold mt-4">
                  Maximum size: 250MB
                </p>
              </div>
            </div>
          </div>
          <div className="mt-4 flex items-center flex-wrap gap-3">
            {videourl &&
              videourl.map((file, index) => {
                const videoSrc = videoSources[index];
                const videoName =
                  typeof file.url === "string"
                    ? file.url.split("/").pop()
                    : file instanceof File
                    ? file.name
                    : typeof file.url.url === "string"
                    ? file.url.url.split("/").pop()
                    : file.url instanceof File
                    ? file.url.name
                    : "";

                return (
                  <div key={index} className="relative">
                    <div className="w-[200px] h-auto">
                      <ReactPlayer
                        url={videoSrc}
                        controls
                        playing={false}
                        width="100%"
                        height="100%"
                        config={{
                          youtube: {
                            playerVars: { rel: 0 },
                          },
                        }}
                        style={{ borderRadius: "18px" }}
                      />
                    </div>

                    <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 p-1">
                      <div className="flex justify-between items-center gap-2">
                        <p className="text-white text-xs w-[170px] truncate">
                          {videoName}
                        </p>
                        <AiOutlineClose
                          className="text-red-500 cursor-pointer"
                          onClick={() =>
                            handleRemoveFile(
                              file instanceof File ? file : file.url,
                              "video"
                            )
                          }
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => setReorderVideoModal(true)}
              className="mt-4 bg-tealBlue text-white px-6 py-2 rounded-lg hover:bg-tealBlue whitespace-nowrap text-[13px] sm:text-base font-bold sm:font-normal"
            >
              Reorder Videos
            </button>
            <button
              onClick={handleSubmit(async (data) => {
                const previewWindow = window.open("", "_blank");
                if (previewWindow) {
                  previewWindow.document.write(
                    "Saving and preparing page preview..."
                  );
                }

                await onSubmit(data);
                if (link && previewWindow) {
                  previewWindow.location.href = `https://${link}`;
                } else if (previewWindow) {
                  previewWindow.close();
                }
              })}
              type="button"
              className="mt-4 inline-block w-[160px] text-center bg-tealBlue text-white px-6 py-2 rounded-lg hover:bg-tealBlue whitespace-nowrap text-[13px] sm:text-base font-bold sm:font-normal"
            >
              Preview
            </button>
          </div>
        </div>
        <div className="mt-4">
          <h4>This text will appear above the videos in the VIDEOS tab</h4>
          <div className="flex items-center space-x-2 pt-2">
            <div className="relative w-full">
              <LexicalEditor
                setComment={(html) => {
                  setValue("videoDescription", html);
                }}
                initialValue={videoDescription || ""}
              />
              {errors?.videoDescription && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.videoDescription.message}
                </p>
              )}
            </div>
          </div>
          {errors?.description && (
            <p className="text-red-500 text-xs mt-1">
              {errors.description.message}
            </p>
          )}
        </div>

        <div className="mt-4">
          <h4>This text will appear above the pdf files in the STORIES tab</h4>
          <div className="flex items-center space-x-2 pt-2">
            <input
              {...register("pdfTitle")}
              className={`w-full text-sm sm:text-base placeholder-gray-500 px-4 border-gray-400 rounded-lg border py-3 focus:outline-none focus:border-blue-400`}
              placeholder="E.g. Obituary"
            />
          </div>
          {errors?.pdfTitle && (
            <p className="text-red-500 text-xs mt-1">
              {errors.pdfTitle.message}
            </p>
          )}
        </div>
        <div className="mt-4">
          <h4>PDF File(s)</h4>
          <div className="flex items-center space-x-2 pt-2">
            <div
              className={`flex w-full justify-center items-center h-52 border-2 bg-blue-50 border-dashed border-tealBlue rounded-lg p-4`}
              {...getPdfRootProps()}
            >
              <input {...getPdfInputProps()} />
              <div className="text-center">
                <ImUpload className="mx-auto text-tealBlue text-6xl" />
                <button
                  type="button"
                  className="mt-4 bg-tealBlue text-white px-6 py-2 rounded-lg hover:bg-tealBlue"
                >
                  Upload PDF
                </button>
                <p className="text-gray-600 text-sm font-bold mt-4">
                  Maximum size: 100MB
                </p>
              </div>
            </div>
          </div>
          <div className="mt-2">
            {acceptedPdfFiles.map((file) => (
              <li
                key={typeof file === "string" ? file : file.path}
                className="flex justify-between items-center gap-2 text-sm break-all
"
              >
                {typeof file === "string" ? file.split("/").pop() : file.path}
                <AiOutlineClose
                  className="text-red-500 cursor-pointer min-w-4"
                  onClick={() => handleRemoveFile(file, "pdf")}
                />
              </li>
            ))}
          </div>

          <div className="mt-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                {...register("isProtected")}
                onChange={(e) => {
                  if (!e.target.checked) {
                    setValue("password", null);
                  }
                  setValue("isProtected", e.target.checked);
                }}
                className="form-checkbox h-4 w-4 text-tealBlue"
              />
              <span className="ml-2 text-sm">
                Do you want to protect this page by secure code?
              </span>
            </label>
          </div>

          {watch("isProtected") && (
            <div className="mt-4">
              <h4>Secure Code</h4>
              <div className="relative">
                <input
                  {...register("password")}
                  type={showPassword ? "text" : "password"}
                  className={`w-full text-sm mt-2 sm:text-base placeholder-gray-500 px-4 pr-10 border-gray-400 rounded-lg border py-3 focus:outline-none focus:border-blue-400 ${
                    errors.password ? "border-red-500" : ""
                  }`}
                  placeholder="Enter secure code"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <AiOutlineEyeInvisible className="h-5 w-5 mt-2 text-gray-500" />
                  ) : (
                    <AiOutlineEye className="h-5 w-5 mt-2 text-gray-500" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.password.message}
                </p>
              )}
            </div>
          )}
        </div>
      </section>
      {acceptedImageFiles.length > 0 && reorderImageModal && (
        <ReorderImageModal
          setReorderImageModal={setReorderImageModal}
          acceptedImageFiles={acceptedImageFiles}
        />
      )}
      {acceptedVideoFiles.length > 0 && reorderVideoModal && (
        <ReorderVideoModal
          setReorderVideoModal={setReorderVideoModal}
          acceptedVideoFiles={acceptedVideoFiles}
        />
      )}
    </>
  );
}
